import { apiClient } from './client';
import { LoginResponse, User } from '../types';
import { clearAccessToken, setAccessToken } from './accessToken';

const PLATFORM_API_URL = (process.env.REACT_APP_PLATFORM_API_URL || 'https://api.innoprog.ru').replace(/\/$/, '');
let launchBrowserNonce = '';
let launchCodeFingerprint = '';
const LAUNCH_NONCE_STORAGE_KEY = 'innoprog:avito-chat:launch-nonce';

const fingerprintLaunchCode = (launchCode: string): string => {
  let hash = 2166136261;
  for (const character of launchCode) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const getLaunchBrowserNonce = (launchCode: string) => {
  const fingerprint = fingerprintLaunchCode(launchCode);
  if (launchBrowserNonce && launchCodeFingerprint === fingerprint) {
    return launchBrowserNonce;
  }
  try {
    const stored = JSON.parse(sessionStorage.getItem(LAUNCH_NONCE_STORAGE_KEY) || '{}');
    if (stored.fingerprint === fingerprint && typeof stored.nonce === 'string' && stored.nonce) {
      launchBrowserNonce = stored.nonce;
      launchCodeFingerprint = fingerprint;
      return launchBrowserNonce;
    }
  } catch {
    sessionStorage.removeItem(LAUNCH_NONCE_STORAGE_KEY);
  }
  if (!launchBrowserNonce || launchCodeFingerprint !== fingerprint) {
    launchBrowserNonce = globalThis.crypto?.randomUUID?.()
      || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    launchCodeFingerprint = fingerprint;
    try {
      sessionStorage.setItem(
        LAUNCH_NONCE_STORAGE_KEY,
        JSON.stringify({ fingerprint, nonce: launchBrowserNonce }),
      );
    } catch {
      // Same-page retries remain safe when storage is unavailable.
    }
  }
  return launchBrowserNonce;
};

export const saveSession = (response: LoginResponse) => {
  setAccessToken(response.access_token);
  localStorage.removeItem('accessToken');
  localStorage.setItem('currentUser', JSON.stringify(response.user));
  localStorage.removeItem('pinCode');
};

export const clearSession = () => {
  clearAccessToken();
  localStorage.removeItem('accessToken');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('pinCode');
};

export const authService = {
  login: async (pinCode: string) => {
    const platformResponse = await fetch(`${PLATFORM_API_URL}/platform/session/login/id`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_id: Number(pinCode) }),
    });
    const platform = await platformResponse.json().catch(() => ({}));
    if (!platformResponse.ok || !platform?.access_token) {
      const error = new Error('Не удалось выполнить вход');
      (error as any).response = {
        status: platformResponse.status,
        data: { detail: platform?.error || 'Не удалось выполнить вход' },
      };
      throw error;
    }
    return (
      await apiClient.post<LoginResponse>(
        '/auth/pin-login',
        { pin_code: pinCode },
        { headers: { Authorization: `Bearer ${platform.access_token}` } },
      )
    ).data;
  },
  exchangeSsoToken: async (token: string) => (await apiClient.post<LoginResponse>('/auth/sso-login', { token })).data,
  exchangeLaunchCode: async (launchCode: string) => (
    await apiClient.post<LoginResponse>('/auth/launch-exchange', {
      launch_code: launchCode,
      browser_nonce: getLaunchBrowserNonce(launchCode),
    })
  ).data,
  getMe: async () => (await apiClient.get<User>('/auth/me')).data,
  logout: async () => {
    await apiClient.post('/auth/logout');
  },
};
